<template>
    <CallDialog ref="dialog" placement="bottom-left" :raised="raised" :labelledby="'np_title' + _uid" @close="$emit('close')">
        <header class="np-header">
            <h2 :id="'np_title' + _uid" class="cdlg-title">Исходящий звонок</h2>
            <div>
                <span class="cdlg-muted">Кому: </span><span>{{ client_line }}</span>
            </div>
        </header>

        <fieldset class="np-rows" :disabled="loading">
            <legend class="sr-only">Выберите номер, с которого звонить</legend>
            <div v-if="loading" class="np-empty">Загрузка номеров…</div>
            <div v-else-if="!providers.length" class="np-empty">Нет доступных исходящих номеров</div>
            <template v-else>
                <div v-for="provider in providers" :key="provider.id" class="np-row">
                    <div class="np-provider">
                        <span class="np-dot" :style="{background: provider.color}" aria-hidden="true"></span>{{ provider.name }}
                    </div>
                    <div class="np-numbers">
                        <label v-for="n in provider.numbers" :key="n.id" :title="n.isMain ? 'Номер по умолчанию' : null">
                            <input v-model="selected_id" type="radio" :name="'np_number' + _uid" :value="n.id" :aria-label="provider.name + ' ' + view(n.number)">
                            {{ view(n.number) }}
                        </label>
                    </div>
                </div>
            </template>
        </fieldset>

        <footer class="np-footer">
            <span class="cdlg-muted" aria-live="polite">{{ providers.length ? summary : '' }}</span>
            <button type="button" class="cdlg-btn cdlg-btn--yes np-call" :disabled="!selection || loading" @click="call">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3 3 5c0 8 8 16 16 16l2-3-5-4-2 2c-3-1-5-3-6-6l2-2z"/></svg>
                <span>Позвонить</span>
            </button>
        </footer>
        <p v-if="error" class="cdlg-error np-error" role="alert">{{ error }}</p>
    </CallDialog>
</template>

<script>
import CallDialog from "./CallDialog";
import outgoingNumbers from "./outgoingNumbers";

// Выбор номера, с которого звонить. Логика (по умолчанию, один выбор на все провайдеры,
// подпись «С номера») — в outgoingNumbers.js, здесь только отображение.
// Окно стоит внизу слева, там же, где панель звонка (см. CallDialog, placement="bottom-left").
export default {
    name: "OutgoingNumberPicker",
    components: {CallDialog},
    props: {
        // Панель звонка сейчас показана (идёт или входит звонок): встать над ней, а не на её место
        raised: {type: Boolean, default: false},
        // Строки провайдеров из outgoingNumbers.groupProviders: только с доступными номерами
        providers: {type: Array, default: () => []},
        loading: {type: Boolean, default: false},
        error: {type: String, default: null},
        clientName: {type: String, default: ''},
        // Телефон клиента уже в том виде, в каком его положено показывать пользователю (маска по правам)
        clientPhone: {type: String, default: ''},
    },
    data: () => ({
        selected_id: null,
    }),
    computed: {
        selection() {
            return outgoingNumbers.select(this.providers, this.selected_id);
        },
        summary() {
            return outgoingNumbers.summary(this.selection);
        },
        client_line() {
            return [this.clientName || 'Клиент', outgoingNumbers.formatPhone(this.clientPhone)].filter(Boolean).join(' · ');
        },
    },
    watch: {
        providers: {
            immediate: true,
            handler(providers) {
                // Выбор не пропадает, если номер остался в списке; иначе — номер по умолчанию,
                // а если его среди доступных нет, не выбрано ничего
                if (outgoingNumbers.select(providers, this.selected_id)) return;
                const def = outgoingNumbers.defaultSelection(providers);
                this.selected_id = def ? def.numberId : null;
            },
        },
        loading(val) {
            if (!val) this.$nextTick(this.focusRadio);
        },
    },
    mounted() {
        if (!this.loading) this.focusRadio();
    },
    methods: {
        view(number) {
            return outgoingNumbers.formatPhone(number);
        },
        focusRadio() {
            const el = this.$el.querySelector('input[type=radio]:checked') || this.$el.querySelector('input[type=radio]');
            if (el) el.focus({preventScroll: true});
        },
        call() {
            if (this.loading || !this.selection) return;
            this.$emit('call', this.selection);
        },
    },
}
</script>

<style lang="scss" scoped>
.np-header {
    position: relative;
    padding: 18px 22px 14px;
}
.np-rows {
    min-width: 0;
    margin: 0;
    padding: 0 22px;
    border: 0;
}
.np-row {
    display: grid;
    grid-template-columns: 135px 1fr;
    gap: 10px;
    align-items: center;
    min-height: 49px;
    border-top: 1px solid #50607570;
}
.np-provider {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.np-dot {
    flex-shrink: 0;
    width: 11px;
    height: 11px;
    background: #a8b5c5;
    border-radius: 50%;
}
.np-numbers {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;

    label {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0;
        padding: 8px 3px;
        font-size: 14px;
        white-space: nowrap;
        cursor: pointer;

        &:hover {
            background: #ffffff08;
            border-radius: 4px;
        }
        &:has(input:focus-visible) {
            outline: 2px solid #51b1ff;
            border-radius: 4px;
        }
    }
    input {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        margin: 0;
        accent-color: #51b1ff;
    }
}
.np-empty {
    padding: 18px 0;
    color: #b6c4d6;
    border-top: 1px solid #50607570;
}
.np-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding: 14px 22px;
    border-top: 1px solid #506075;
}
.np-call {
    padding: 11px 23px;
}
.np-error {
    padding: 0 22px 12px;
}

// Окно шириной с панель звонка (700px): три номера в ряд помещаются, на экране уже — два, потом один
@media (max-width: 680px) {
    .np-numbers {
        grid-template-columns: repeat(2, minmax(0, 1fr));

        label { font-size: 13px; }
    }
    .np-row {
        grid-template-columns: 100px 1fr;
        padding: 5px 0;
    }
}
@media (max-width: 480px) {
    .np-header { padding: 16px; }
    .np-rows { padding: 0 16px; }
    .np-numbers { grid-template-columns: 1fr; }
    .np-row {
        grid-template-columns: 85px 1fr;
        align-items: start;
    }
    .np-provider { padding-top: 10px; }
    .np-footer {
        flex-direction: column;
        align-items: stretch;
        padding: 14px 16px;
    }
    .np-call { justify-content: center; }
}
</style>
